import './search'
import { GMXSearch } from './search'
import { GMXSearchResults } from './search-results'

/**
 * Site Search
 *
 * Thin wrapper around <gmx-search> that wires up the standard search markup
 * and lets the host page configure endpoints and i18n strings via data-* attributes.
 *
 * ## Integration Patterns
 *
 * ### Pattern A: Navigation (Cross-page)
 * User searches on Page A, navigates to Page B where results are displayed.
 *
 * **Page A (e.g., homepage):**
 * ```html
 * <gmx-site-search
 *   data-results-page-url="/site-search"
 *   data-autocomplete-path="/api/autocomplete"
 * ></gmx-site-search>
 * ```
 *
 * **Page B (/site-search):**
 * ```html
 * <gmx-search-results
 *   data-search-api-path="/api/search"
 * ></gmx-search-results>
 * ```
 *
 * When user searches, they navigate to `/site-search?q=query`. The `<gmx-search-results>`
 * component automatically reads the query from the URL and fetches results.
 *
 * ### Pattern B: Same-page
 * User searches and results appear on the same page without navigation.
 *
 * ```html
 * <gmx-site-search
 *   data-search-results-id="my-results"
 *   data-autocomplete-path="/api/autocomplete"
 * ></gmx-site-search>
 *
 * <gmx-search-results
 *   id="my-results"
 *   data-search-api-path="/api/search"
 * ></gmx-search-results>
 * ```
 *
 * When user searches, the component finds `<gmx-search-results id="my-results">`
 * and calls its `.search()` method directly. No navigation occurs.
 *
 * ### Using Both (Fallback)
 * You can set both attributes for graceful degradation:
 *
 * ```html
 * <gmx-site-search
 *   data-search-results-id="my-results"
 *   data-results-page-url="/site-search"
 *   data-autocomplete-path="/api/autocomplete"
 * ></gmx-site-search>
 * ```
 *
 * - If `#my-results` exists on the page → same-page pattern (no navigation)
 * - If `#my-results` is NOT found → falls back to navigation pattern
 *
 * ## Data Attributes
 *
 * ### Search Configuration
 * - `data-search-results-id` - ID of `<gmx-search-results>` element for same-page search
 * - `data-results-page-url` - Page URL to navigate to when search is submitted (cross-page pattern)
 * - `data-autocomplete-path` - API endpoint for autocomplete suggestions
 * - `data-autocomplete-max` - Max autocomplete results (default: 20)
 * - `data-theme` - Theme variant: "light" or "dark"
 *
 * ### Input Configuration
 * - `data-input-id` - Custom ID for the input element (auto-generated if not provided)
 * - `data-input-name` - Name attribute for the input (default: "search")
 * - `data-placeholder` - Placeholder text (default: "What can we help you with?")
 *
 * On connect, the component reads the URL query parameter `q` (if present) and
 * uses it as the search input's initial value.
 *
 * ### Common Topics
 * - `data-common-topics` - JSON array of topic objects (e.g., '[{"label":"topic1"},{"label":"topic2"}]')
 * - `data-common-topics-label` - Label text before topics (default: "Common topics:")
 *
 * ### i18n / Accessibility Labels
 * - `data-input-aria-label` - ARIA label for input (default: "search")
 * - `data-clear-btn-aria-label` - ARIA label for clear button (default: "Clear search")
 * - `data-search-btn-aria-label` - ARIA label for search button (default: "Search")
 *
 * Note: No brand-specific defaults are applied for endpoints; if required
 * attributes such as data-results-page-url or data-autocomplete-path are not
 * provided, the component will not make search/autocomplete requests.
 */
export class GMXSiteSearch extends HTMLElement {
	private searchEl: GMXSearch | null = null
	private boundSearchHandler = this._handleSearch.bind(this)

	private _escapeAttr(value: string): string {
		return value
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#39;')
	}

	/**
	 * Reads and trims the `q` query string parameter from the current URL.
	 * Returns an empty string when missing or blank.
	 */
	private _getInitialQueryFromUrl(): string {
		const query = new URLSearchParams(window.location.search).get('q')
		return query?.trim() || ''
	}

	/**
	 * Sets the search input value from the URL query and dispatches focus/blur
	 * so child styles (e.g. search icon visibility) update without opening autocomplete.
	 */
	private _setInputValue(value: string): void {
		if (!this.searchEl || !value) return

		const input = this.searchEl.querySelector('input[type="search"]') as HTMLInputElement | null
		if (!input) return

		if (!input.value) {
			input.value = value
			input.classList.add('filled')
		}

		input.dispatchEvent(new FocusEvent('focus'))
		input.dispatchEvent(new FocusEvent('blur'))
	}

	connectedCallback(): void {
		// Avoid double-initialization
		if (this.searchEl) return

		// Respect any existing inner content if author provided a custom <gmx-search>
		const existingSearch = this.querySelector('gmx-search') as GMXSearch | null

		const initialQuery = this._getInitialQueryFromUrl()

		if (existingSearch) {
			this.searchEl = existingSearch
			this._setInputValue(initialQuery)
		} else {
			this.searchEl = document.createElement('gmx-search') as GMXSearch
			this.searchEl.classList.add('gmx-form-field-container', 'gmx-search')

			// Generate a unique input ID or use provided one
			const inputId = this._escapeAttr(
				this.getAttribute('data-input-id') || `gmx-search-input-${Math.random().toString(36).substring(2, 11)}`
			)
			const inputName = this._escapeAttr(this.getAttribute('data-input-name') || 'search')
			const placeholder = this._escapeAttr(this.getAttribute('data-placeholder') || 'What can we help you with?')
			const inputAriaLabel = this._escapeAttr(this.getAttribute('data-input-aria-label') || 'search')
			const clearBtnAriaLabel = this._escapeAttr(this.getAttribute('data-clear-btn-aria-label') || 'Clear search')
			const searchBtnAriaLabel = this._escapeAttr(this.getAttribute('data-search-btn-aria-label') || 'Search')
			const valueAttr = initialQuery ? `value="${this._escapeAttr(initialQuery)}"` : ''

			this.searchEl.innerHTML = `
				<div class="gmx-search-container">
					<div class="gmx-search-wrapper">
						<span class="search-icon" aria-hidden="true"></span>
						<input
							id="${inputId}"
							name="${inputName}"
							type="search"
							aria-label="${inputAriaLabel}"
							placeholder="${placeholder}"
							${valueAttr}
						/>
						<button
							type="button"
							class="cancel-btn"
							data-icon-name="close"
							aria-label="${clearBtnAriaLabel}"
						></button>
					</div>
					<button
						type="button"
						class="gmx-btn gmx-btn-secondary icon-only size-lg search-btn"
						data-icon-name="arrow"
						aria-label="${searchBtnAriaLabel}"
					></button>
				</div>
				<div class="autocomplete-dropdown"></div>
			`

			this.appendChild(this.searchEl)
			this._setInputValue(initialQuery)
		}

		const wrapperAutocompletePath = this.getAttribute('data-autocomplete-path')
		const wrapperAutocompleteMax = this.getAttribute('data-autocomplete-max')
		const wrapperResultsPageUrl = this.getAttribute('data-results-page-url')
		const wrapperLoadMorePath = this.getAttribute('data-search-load-more-path')
		const wrapperPageSize = this.getAttribute('data-search-page-size')
		const wrapperCommonTopics = this.getAttribute('data-common-topics')

		if (wrapperAutocompletePath && !this.searchEl.hasAttribute('data-autocomplete-path')) {
			this.searchEl.setAttribute('data-autocomplete-path', wrapperAutocompletePath)
		}

		if (wrapperAutocompleteMax && !this.searchEl.hasAttribute('data-autocomplete-max')) {
			this.searchEl.setAttribute('data-autocomplete-max', wrapperAutocompleteMax)
		}

		if (wrapperResultsPageUrl && !this.searchEl.hasAttribute('data-results-page-url')) {
			this.searchEl.setAttribute('data-results-page-url', wrapperResultsPageUrl)
		}

		if (wrapperLoadMorePath && !this.searchEl.hasAttribute('data-search-load-more-path')) {
			this.searchEl.setAttribute('data-search-load-more-path', wrapperLoadMorePath)
		}

		if (wrapperPageSize && !this.searchEl.hasAttribute('data-search-page-size')) {
			this.searchEl.setAttribute('data-search-page-size', wrapperPageSize)
		}

		// Optional common topics section
		// Configured via data-common-topics='[{"label":"topic1"},{"label":"topic2"}]' (JSON array)
		// These should be authored inline as they are content-managed strings, not API-driven
		if (wrapperCommonTopics) {
			try {
				const topics = JSON.parse(wrapperCommonTopics) as { label: string }[]
				if (Array.isArray(topics)) {
					this._renderCommonTopics(topics)
				}
			} catch {
				// Invalid JSON, skip rendering common topics
			}
		}

		// Pass through theme from wrapper to inner search component
		const theme = this.getAttribute('data-theme')
		if (theme && !this.searchEl.hasAttribute('data-theme')) {
			this.searchEl.setAttribute('data-theme', theme)
		}

		// Listen for search events to handle same-page pattern with data-search-results-id
		this.searchEl.addEventListener('search', this.boundSearchHandler)
	}

	disconnectedCallback(): void {
		if (this.searchEl) {
			this.searchEl.removeEventListener('search', this.boundSearchHandler)
		}
	}

	/**
	 * Handles search events from the inner gmx-search component.
	 * If data-search-results-id is set and the element exists, performs same-page search.
	 * Otherwise, allows the default navigation behavior.
	 */
	private _handleSearch(event: Event): void {
		const searchResultsId = this.getAttribute('data-search-results-id')
		if (!searchResultsId) return // Let navigation pattern handle it

		const searchResultsEl = document.getElementById(searchResultsId) as GMXSearchResults | null
		if (!searchResultsEl || typeof searchResultsEl.search !== 'function') {
			// Element not found or not a valid gmx-search-results, fall back to navigation
			return
		}

		// Prevent the default navigation behavior
		event.preventDefault()

		// Get the query from the event detail
		const customEvent = event as CustomEvent<{ query: string }>
		const query = customEvent.detail?.query
		if (query) {
			searchResultsEl.search(query)
		}
	}

	private _renderCommonTopics(topics: { label: string }[]): void {
		if (!this.searchEl || !topics.length) return

		// Get configurable label text for i18n
		const labelText = this.getAttribute('data-common-topics-label') || 'Common topics:'
		const isDarkTheme = this.getAttribute('data-theme') === 'dark'

		const container = document.createElement('gmx-common-topics')
		container.className = ''
		// Use role="region" to make aria-label valid on the div element
		container.setAttribute('role', 'region')
		container.setAttribute('aria-label', labelText.replace(/:$/, ''))

		if (isDarkTheme) {
			container.classList.add('gmx-invert')
		}

		const label = document.createElement('span')
		label.className = 'gmx-common-topic-label'

		label.textContent = `${labelText} `
		container.appendChild(label)

		// Render topics
		topics.forEach((topic, index) => {
			const btn = document.createElement('button')
			btn.type = 'button'
			btn.textContent = topic.label
			btn.className = 'gmx-btn gmx-btn-link-secondary'

			btn.addEventListener('click', () => {
				this.searchEl?.search(topic.label)
			})
			container.appendChild(btn)
			if (index < topics.length - 1) {
				container.appendChild(document.createTextNode(', '))
			}
		})

		// Insert common topics section after the search element in the DOM
		this.appendChild(container)
	}
}

if (!customElements.get('gmx-site-search')) {
	customElements.define('gmx-site-search', GMXSiteSearch)
}
